import { useState, useEffect, useCallback, useRef } from 'preact/hooks';

interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  loading: boolean;
  error: string | null;
  setPage: (page: number) => void;
  setSearch: (search: string) => void;
  search: string;
  refresh: () => void;
}

type Fetcher<T> = (page: number, search: string) => Promise<{
  items: T[];
  total: number;
}>;

export function usePaginatedList<T>(fetcher: Fetcher<T>): PaginatedResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const load = useCallback(async (p: number, s: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetcherRef.current(p, s);
      setItems(result.items);
      setTotal(result.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch when page, search, OR fetcher identity changes
  useEffect(() => {
    load(page, search);
  }, [page, search, load, fetcher]);

  const refresh = useCallback(() => {
    load(page, search);
  }, [load, page, search]);

  const handleSetSearch = useCallback((s: string) => {
    setPage(1);
    setSearch(s);
  }, []);

  return { items, total, page, loading, error, setPage, setSearch: handleSetSearch, search, refresh };
}
